import add_blue from 'images/icon/add_blue.png';
import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router';
import swal from 'sweetalert2';
import OrderCreation from '../../../components_v2/creation/OrderCreation';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import InputSearch from '../../../components_v2/input/InputSearch';
import PopupExport from '../../../components_v2/popup/PopupExport';
import storeLang from '../../../helpers/storeLang';
import {
	PrimaryColor,
	DarkGreySidely2,
	GreySidely3,
	PastelPrimaryColor,
	RedSidely,
	SidelyBlack
} from '../../../styles/global/css/Utils';
import {
	ToolbarBox,
	ToolbarImageContainer
} from '../../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import Restricted from '../../permissions/Restricted';
import usePermission from '../../permissions/usePermission';
import { deleteOrders, exportOrders } from '../data/Data';
import { cleanOrderCreator, getOrderCreator } from '../global/globalFunction';
import { Order, TypeFile } from '../model/Model';
import { Container } from './style/Style';
import TemplateOrdersKanban from './subPage/TemplateOrdersKanban';
import TemplateOrdersList from './subPage/TemplateOrdersList';
import { Ellipsis, LayoutGrid, Menu } from 'lucide-react';

function warningOrder(
	setRedirect: (...args: unknown[]) => void,
	setUpdated: (...args: unknown[]) => void
): void {
	const translate = getTranslate(storeLang.getState().localize);

	swal({
		text: translate('orders.fire.warningExistOrder').toString(),
		type: 'warning',
		confirmButtonText: translate('orders.fire.warningContinue').toString(),
		confirmButtonColor: PrimaryColor,
		cancelButtonText: translate('orders.fire.warningClear').toString(),
		cancelButtonColor: RedSidely,
		showCancelButton: true
	}).then(result => {
		if (result.value) {
			setRedirect(<Redirect push to="/orders/select-products" />);
		}

		if (result.dismiss && result.dismiss === swal.DismissReason.cancel) {
			cleanOrderCreator().then(() => {
				setUpdated(true);
			});
		}
	});
}

function selectionDelete(
	idOrders: number[],
	setUpdated: (...args: unknown[]) => void
): void {
	const translate = getTranslate(storeLang.getState().localize);

	if (idOrders.length <= 0) {
		return;
	}

	swal({
		text: translate('global.fire.alert_title').toString(),
		type: 'warning',
		confirmButtonText: translate('global.fire.delete_message')
			.toString()
			.replace('{{NAME}}', `${idOrders.length} orders`),
		confirmButtonColor: RedSidely
	}).then(result => {
		if (result.value) {
			deleteOrders(idOrders).then(() => {
				setUpdated(true);
			});
		}
	});
}

function initToolbar(
	setToolBarState: (value: ToolbarState) => void,
	setOpen: (...args: unknown[]) => void,
	listMode: boolean,
	setListMode: (...args: unknown[]) => void,
	setRedirect: (...args: unknown[]) => void,
	setExportOpen: (...args: unknown[]) => void,
	setSearchValue: (...args: unknown[]) => void,
	selectedOrders: Order[],
	setUpdated: (...args: unknown[]) => void,
	deleteAllowed: boolean,
	exportAllowed: boolean
): void {
	const translate = getTranslate(storeLang.getState().localize);

	const data: DropdownData[] = [];
	if (deleteAllowed) {
		data.push({ label: 'Delete selection', value: 'delete' });
	}

	if (exportAllowed) {
		data.push({ label: 'export', value: 'export' });
	}

	setToolBarState({
		title: translate('orders.title').toString(),
		bottomLeftToolbarComponent: <></>,
		bottomRightToolbarComponent: (
			<Restricted to={{ objectAction: 'ReadOrder' }}>
				<ToolbarBox>
					<InputSearch
						name={'search_bar'}
						type={'text'}
						placeholder={translate('search').toString()}
						onChange={value => setSearchValue(value)}
						delay={500}
					/>
					<ToolbarImageContainer
						marginRight="7px"
						hasPointer
						onClick={() => {
							setListMode(true);
						}}
					>
						<Menu color={listMode ? SidelyBlack : DarkGreySidely2} />
					</ToolbarImageContainer>
					<ToolbarImageContainer
						hasPointer
						onClick={() => {
							setListMode(false);
						}}
					>
						<LayoutGrid color={!listMode ? SidelyBlack : DarkGreySidely2} />
					</ToolbarImageContainer>

					<Restricted to={{ objectAction: 'CreateOrder' }}>
						<ToolbarImageContainer
							marginLeft="10px"
							hasPointer
							backgroundColor={PastelPrimaryColor}
							round
							onClick={() => {
								const order = getOrderCreator();
								if (order.company || order.creationDate || order.name) {
									warningOrder(setRedirect, setOpen);
								} else {
									setOpen(true);
								}
							}}
						>
							<img style={{ height: '25px', width: '25px' }} src={add_blue} />
						</ToolbarImageContainer>
					</Restricted>

					<Restricted
						to={[
							{ objectAction: 'DeleteOrder' },
							{ objectAction: 'CreateExport' }
						]}
					>
						<Dropdown
							dropdownStyle={{ optionLeft: '-115px', optionWidth: '160px' }}
							datalist={data}
							name={'options'}
							JSXButton={() => (
								<ToolbarImageContainer marginLeft="10px" hasPointer>
									<Ellipsis size={18} color={GreySidely3} strokeWidth={2.5} />
								</ToolbarImageContainer>
							)}
							searchable={false}
							onChange={(value: DropdownData) => {
								switch (value.value) {
									case 'export':
										setExportOpen(true);
										break;
									case 'delete':
										selectionDelete(selectedOrders.map(o => o.id), setUpdated);
										break;
									default:
										break;
								}
							}}
						/>
					</Restricted>
				</ToolbarBox>
			</Restricted>
		)
	});
}

function TemplateOrders(props: {
  setToolBarState: (value: ToolbarState) => void;
}): JSX.Element {
	const [listMode, setListMode] = React.useState<boolean>(true);
	const [isOpen, setOpen] = React.useState<boolean>(false);
	const [isExportOpen, setExportOpen] = React.useState<boolean>(false);

	const [searchValue, setSearchValue] = React.useState<string>('');
	const [redirect, setRedirect] = React.useState<JSX.Element>(<></>);

	const [selectedOrders, setSelectedOrders] = React.useState<Order[]>([]);
	const [updated, setUpdated] = React.useState<boolean>(false);

	// permissions
	const deleteAllowed = usePermission({ objectAction: 'DeleteOrder' });
	const exportAllowed = usePermission({ objectAction: 'CreateExport' });

	const [clientCompanyId, setClientCompanyId] = React.useState<number>();
	const dispatch = useDispatch();

	React.useEffect(() => {
		const queryParams = new URLSearchParams(window.location.search);
		const paramId = queryParams.get('create');
		if (paramId) {
			const companyId: number = parseInt(paramId);
			if (!isNaN(companyId)) {
				setClientCompanyId(companyId);
				setOpen(true);
			}
		}
	}, []);

	React.useEffect(
		() => {
			setUpdated(false);
		},
		[updated]
	);

	React.useEffect(
		() => {
			initToolbar(
				props.setToolBarState,
				setOpen,
				listMode,
				setListMode,
				setRedirect,
				setExportOpen,
				setSearchValue,
				selectedOrders,
				setUpdated,
				deleteAllowed,
				exportAllowed
			);
		},
		[listMode, selectedOrders]
	);

	return (
		<Restricted to={{ objectAction: 'ReadOrder' }}>
			<Container>
				{redirect}

				<OrderCreation
					clientCompanyId={clientCompanyId}
					isOpen={isOpen}
					setOpen={setOpen}
				/>

				<PopupExport
					exportExcel
					exportCSV
					isOpen={isExportOpen}
					onClickOut={() => setExportOpen(false)}
					onClickExport={value => {
						setExportOpen(false);
						if (value == null) {
							return;
						}

						if (value.value === TypeFile.PDF && selectedOrders.length <= 0) {
							return;
						}

						exportOrders(
							value.value,
							'Orders',
							value.value === TypeFile.PDF ? selectedOrders[0].id : undefined
						).then(response => {
							if (response.valueOf()) {
								dispatch({
									type: 'INSERT_MESSAGE',
									value: {
										message: { text: 'exports.ready_toast' },
										state: 'loading'
									}
								});
							}
						});
					}}
				/>
				{listMode ? (
					<TemplateOrdersList
						searchValue={searchValue}
						onSelectedOrders={values => setSelectedOrders(values)}
						updated={updated}
						setRedirect={setRedirect}
					/>
				) : (
					<TemplateOrdersKanban searchValue={searchValue} />
				)}
			</Container>
		</Restricted>
	);
}

export default TemplateOrders;
